import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { ArrowLeft, Sparkles, AlertTriangle, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SignalForm } from "@/components/admin/SignalForm";
import { createSignal, generateSignalId, type SignalFormValues } from "@/lib/admin-mutations";
import { dispatchPushEvent } from "@/lib/push.functions";
import { authedFetch } from "@/lib/authed-fetch";
import { supabase } from "@/integrations/supabase/client";
import type { AnalystReport } from "@/lib/analyst.functions";
import { fetchDraftById, markDraftPublished, type SignalDraft } from "@/lib/signal-drafts";

const newSignalSearchSchema = z.object({
  draft: z.string().min(1).max(80).optional(),
});

export const Route = createFileRoute("/_authenticated/admin/signals/new")({
  validateSearch: (search) => newSignalSearchSchema.parse(search),
  component: NewSignalPage,
});

const PENDING_KEY = "xaupro:analyst:pendingSignal";

type PendingPayload = {
  report: AnalystReport;
  chartDataUrl: string | null;
  createdAt: number;
};

/** Pull the first numeric token from a free-form string like "TP1: $ 2,345.50 to 2,348.00". */
function parseFirstNumber(input: string | null | undefined): number {
  if (!input) return 0;
  // Strip TP/Take Profit labels first so "TP1: 2,355" doesn't parse as 1.
  // Then strip commas, whitespace, and currency symbols.
  const cleaned = input
    .replace(/TP\s*\d+|Take\s*Profit\s*\d*/gi, "")
    .replace(/[,\s$]/g, "");
  const m = cleaned.match(/-?\d+(?:\.\d+)?/);
  return m ? Number(m[0]) : 0;
}

/** Best-effort timeframe inference from the AI report's free-form text. */
function parseTimeframe(r: AnalystReport): "15M" | "1H" | "3H" | "4H" | "8H" | "Daily" | "Weekly" | undefined {
  const hay = [
    r.key_zone,
    r.summary,
    r.reasoning,
    ...(r.structure ?? []),
    ...(r.liquidity ?? []),
  ]
    .filter(Boolean)
    .join(" ")
    .toUpperCase();
  // Check higher timeframes first so longer tokens win.
  if (/\b(WEEKLY|1W|W1)\b/.test(hay)) return "Weekly";
  if (/\b(DAILY|1D|D1)\b/.test(hay)) return "Daily";
  if (/\b(8H|H8|480M|12H|H12)\b/.test(hay)) return "8H";
  if (/\b(4H|H4|240M)\b/.test(hay)) return "4H";
  if (/\b(3H|H3|180M)\b/.test(hay)) return "3H";
  if (/\b(2H|H2|1H|H1|60M)\b/.test(hay)) return "1H";
  if (/\b(15M|M15|30M|M30|5M|M5)\b/.test(hay)) return "15M";
  return undefined;
}

/** Trading style: prefer AI value, fall back to standard TF buckets. */
function pickStyle(
  r: AnalystReport,
  tf: ReturnType<typeof parseTimeframe>,
): "Scalp" | "Intraday" | "Swing" | undefined {
  const ai = r.trade_setup.trading_style;
  if (ai === "SCALP") return "Scalp";
  if (ai === "INTRADAY") return "Intraday";
  if (ai === "SWING") return "Swing";
  if (tf === "15M") return "Scalp";
  if (tf === "1H") return "Intraday";
  if (tf === "4H" || tf === "Daily" || tf === "Weekly") return "Swing";
  return undefined;
}

type Hydrated = {
  initial: Partial<SignalFormValues>;
  chartDataUrl: string | null;
  isWait: boolean;
  baseNote: string;
};

function buildInitial(payload: PendingPayload): Hydrated {
  const r = payload.report;
  const ts = r.trade_setup;
  const isWait = ts.direction === "WAIT";
  // Note contains ONLY the AI reasoning paragraph. Key zone & invalidation are
  // intentionally excluded so the textarea stays clean and editable.
  const baseNote = (r.reasoning ?? "").slice(0, 1000);

  const tf = parseTimeframe(r);
  const asset = (r.asset && r.asset.trim()) ? r.asset.trim().toUpperCase() : "XAUUSD";
  const style = pickStyle(r, tf);

  // Order type: prefer AI value, otherwise infer from entry_zone wording.
  const ot = inferOrderType(ts.order_type, ts.entry_zone, isWait);

  const initial: Partial<SignalFormValues> = {
    asset,
    entry: parseFirstNumber(ts.entry_zone),
    sl: parseFirstNumber(ts.sl),
    tp1: parseFirstNumber(ts.tp[0]),
    tp2: parseFirstNumber(ts.tp[1] ?? ts.tp[0]),
    note: baseNote,
    ...(tf ? { timeframe: tf } : {}),
    ...(ot ? { order_type: ot } : {}),
    ...(style ? { style } : {}),
  };
  // Only seed direction when AI is confident. WAIT forces a manual choice.
  if (!isWait) {
    initial.type = ts.direction === "SHORT" ? "Sell" : "Buy";
  }
  return { initial, chartDataUrl: payload.chartDataUrl, isWait, baseNote };
}

/** Map AI uppercase enum to form title case, with heuristic fallback. */
function inferOrderType(
  raw: "MARKET" | "LIMIT" | "STOP" | null | undefined,
  entryZone: string | null | undefined,
  isWait: boolean,
): "Market" | "Limit" | "Stop" | undefined {
  if (raw === "MARKET") return "Market";
  if (raw === "LIMIT") return "Limit";
  if (raw === "STOP") return "Stop";
  if (isWait) return undefined; // let form default apply
  const hay = (entryZone ?? "").toUpperCase();
  if (/(BREAKOUT|BREAK\s*OF|STOP\s*RUN|BREAK\s*OUT)/.test(hay)) return "Stop";
  if (/(RETRACEMENT|PULLBACK|\bOB\b|\bFVG\b|DISCOUNT|PREMIUM|ORDER\s*BLOCK|FAIR\s*VALUE)/.test(hay)) return "Limit";
  // Range like "2,345 to 2,348" usually indicates a wait-for-pullback zone.
  if (/\d[\d,\.]*\s*(TO|-|–|—)\s*\d/.test(hay)) return "Limit";
  return "Market";
}

/** Convert a data URL to a Blob without using fetch (works in any environment). */
function dataUrlToBlob(dataUrl: string): { blob: Blob; ext: string } | null {
  const m = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
  if (!m) return null;
  const mime = m[1];
  const b64 = m[2];
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const ext = mime.split("/")[1]?.split("+")[0] || "png";
  return { blob: new Blob([bytes], { type: mime }), ext };
}

function draftToInitial(d: SignalDraft): Hydrated {
  const noteWithRationale = d.note?.trim()
    ? d.note
    : d.ai_rationale
      ? `AI rationale: ${typeof d.ai_rationale === "string" ? d.ai_rationale : JSON.stringify(d.ai_rationale).slice(0, 400)}`
      : "";
  return {
    initial: {
      asset: d.asset,
      type: d.type,
      order_type: d.order_type,
      timeframe: d.timeframe,
      style: d.style,
      entry: d.entry,
      sl: d.sl,
      tp1: d.tp1,
      tp2: d.tp2,
      note: noteWithRationale,
    },
    chartDataUrl: null,
    isWait: false,
    baseNote: noteWithRationale,
  };
}

function NewSignalPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { draft: draftId } = Route.useSearch();

  const [hydrated, setHydrated] = useState<Hydrated | null>(null);
  const [chartUrl, setChartUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const draftQ = useQuery({
    queryKey: ["signal-draft", draftId],
    queryFn: () => (draftId ? fetchDraftById(draftId) : Promise.resolve(null)),
    enabled: !!draftId,
  });

  // Hydrate from a loaded draft (preferred when ?draft= is present).
  useEffect(() => {
    if (!draftQ.data) return;
    if (draftQ.data.status !== "pending") {
      toast.info(`This draft is already ${draftQ.data.status}.`);
      navigate({ to: "/admin/signals/drafts" });
      return;
    }
    setHydrated(draftToInitial(draftQ.data));
    toast.success(`Pre-filled from AI draft (${draftQ.data.confidence_pct}% confidence).`);
  }, [draftQ.data, navigate]);

  // Hydrate from sessionStorage (the AI Analyst flow). Skip when ?draft= is present.
  useEffect(() => {
    if (draftId) return;
    try {
      const raw = sessionStorage.getItem(PENDING_KEY);
      if (!raw) return;
      sessionStorage.removeItem(PENDING_KEY);
      const payload = JSON.parse(raw) as PendingPayload;
      if (!payload?.report) return;
      const h = buildInitial(payload);
      setHydrated(h);
      toast.success("AI analysis pre-filled. Review levels before publishing.");
    } catch (e) {
      console.error("Could not hydrate pending signal", e);
    }
  }, [draftId]);

  // Background upload of the chart image, then bake its URL into the note.
  useEffect(() => {
    if (!hydrated?.chartDataUrl) return;
    let cancelled = false;
    const run = async () => {
      setUploading(true);
      try {
        const { data: sessionData } = await supabase.auth.getUser();
        const userId = sessionData.user?.id;
        if (!userId) throw new Error("Not authenticated");
        const parsed = dataUrlToBlob(hydrated.chartDataUrl!);
        if (!parsed) throw new Error("Invalid chart image");
        const path = `${userId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${parsed.ext}`;
        const { error: upErr } = await supabase.storage
          .from("analyst-charts")
          .upload(path, parsed.blob, { contentType: parsed.blob.type, upsert: false });
        if (upErr) throw upErr;
        const { data: pub } = supabase.storage.from("analyst-charts").getPublicUrl(path);
        if (cancelled) return;
        setChartUrl(pub.publicUrl);
      } catch (err) {
        console.error("Chart upload failed", err);
        if (!cancelled) toast.warning("Chart image could not be uploaded. You can still publish without it.");
      } finally {
        if (!cancelled) setUploading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [hydrated?.chartDataUrl]);

  const m = useMutation({
    mutationFn: async (values: SignalFormValues) => {
      const id = await generateSignalId(values.asset);
      // Guard: if a draft is in the URL, refuse to publish until the draft has loaded,
      // otherwise we'd insert the live row with NULL macro fields.
      if (draftId && !draftQ.data) {
        throw new Error("Draft is still loading. Please wait a moment and try again.");
      }
      const macro = draftQ.data
        ? { alignment: draftQ.data.macro_alignment, context: draftQ.data.macro_context }
        : undefined;
      const source = draftQ.data?.source === "ai" ? "orion" : undefined;
      const quality = draftQ.data
        ? { grade: draftQ.data.quality_grade, reasons: draftQ.data.grade_reasons }
        : undefined;
      const publishedId = await createSignal({ ...values, id }, macro, source, quality);
      if (draftId) {
        try {
          await markDraftPublished(draftId, publishedId);
        } catch (err) {
          // Signal is live, but draft mark failed. Warn but do not throw.
          console.warn("Could not mark draft as published", err);
          toast.warning("Signal published, but draft status update failed. You can ignore the draft manually.");
        }
      }
      return publishedId;
    },
    onSuccess: async (id) => {
      toast.success(`Signal published: ${id}`);
      qc.invalidateQueries({ queryKey: ["admin"] });
      qc.invalidateQueries({ queryKey: ["live-signals"] });
      qc.invalidateQueries({ queryKey: ["public-signals"] });
      qc.invalidateQueries({ queryKey: ["active-signal-count"] });
      qc.invalidateQueries({ queryKey: ["signal-drafts"] });
      qc.invalidateQueries({ queryKey: ["signal-drafts-count"] });
      dispatchPushEvent({ data: { signalId: id, event: "posted" }, fetch: authedFetch }).catch((e) =>
        console.warn("push dispatch failed", e),
      );
      navigate({ to: "/admin/signals" });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const fromDraft = !!draftId && !!draftQ.data;

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm">
          <Link to={fromDraft ? "/admin/signals/drafts" : "/admin/signals"}>
            <ArrowLeft className="h-4 w-4" /> Back to {fromDraft ? "drafts" : "signals"}
          </Link>
        </Button>
        <h1 className="mt-2 font-display text-3xl font-bold">New signal</h1>
        <p className="mt-1 text-sm text-muted-foreground">Publish to the live feed (members), the public feed, or both.</p>
      </div>

      {fromDraft && draftQ.data && (
        <div className="rounded-xl border border-gold/40 bg-gold-muted/20 p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-gold shadow-gold">
              <Inbox className="h-4 w-4 text-gold-foreground" />
            </div>
            <div className="flex-1">
              <div className="font-display text-sm font-bold uppercase tracking-wider text-gold">
                AI Draft Review ({draftQ.data.confidence_pct}% confidence)
              </div>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                Review levels and tune the note. Publishing will mark this draft as published in the inbox.
              </p>
            </div>
          </div>
        </div>
      )}

      {!fromDraft && hydrated && (
        <div className="space-y-3 rounded-xl border border-gold/40 bg-gold-muted/20 p-4">
          <div className="flex items-start gap-4">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-gold shadow-gold">
              <Sparkles className="h-4 w-4 text-gold-foreground" />
            </div>
            <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-start">
              <div className="flex-1">
                <div className="font-display text-sm font-bold uppercase tracking-wider text-gold">
                  Pre-filled from AI Analysis
                </div>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  Levels were parsed from the AI report. Review and refine before publishing.
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {uploading
                    ? "Uploading chart image..."
                    : chartUrl
                      ? "Chart attached to note as a Markdown image."
                      : hydrated.chartDataUrl
                        ? "Chart upload pending."
                        : null}
                </p>
              </div>
              {(chartUrl || hydrated.chartDataUrl) && (
                <img
                  src={chartUrl ?? hydrated.chartDataUrl ?? undefined}
                  alt="Analyzed chart"
                  className="max-h-24 rounded-md border border-gold/30 object-contain"
                />
              )}
            </div>
          </div>
          {hydrated.isWait && (
            <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>
                AI flagged this as <strong>WAIT</strong>. Confirm the direction (Buy or Sell) manually before publishing.
              </span>
            </div>
          )}
        </div>
      )}

      <SignalForm
        hideId
        initial={hydrated?.initial}
        requireTypeChoice={hydrated?.isWait ?? false}
        onSubmit={(values) => m.mutate(values)}
        submitting={m.isPending || (!!draftId && draftQ.isPending)}
        submitLabel="Publish signal"
      />
    </div>
  );
}
